<template>
    <div class="ppv2-relative">
        <button
            @click="toggleDropdown"
            :class="[
                'ppv2-flex ppv2-items-center ppv2-gap-2 ppv2-border ppv2-border-solid ppv2-rounded-lg ppv2-px-3 ppv2-py-2 ppv2-text-sm ppv2-transition-colors',
                isActive
                    ? 'ppv2-bg-purple-50 ppv2-border-purple-200 ppv2-text-purple-700'
                    : 'ppv2-border-black ppv2-border-opacity-20 ppv2-text-black ppv2-text-opacity-65 hover:ppv2-bg-black hover:ppv2-bg-opacity-5',
            ]"
        >
            <slot name="icon"></slot>
            <span>{{ label }}</span>
            <span v-if="selectedCount > 0" class="ppv2-bg-blue-600 ppv2-text-white ppv2-text-xs ppv2-rounded-full ppv2-px-1.5">
                {{ selectedCount }}
            </span>
            <ChevronDown :size="16" :class="{ 'ppv2-rotate-180': isOpen }" class="ppv2-transition-transform" />
        </button>

        <div
            v-if="isOpen"
            class="ppv2-absolute ppv2-top-full ppv2-left-0 ppv2-mt-1 ppv2-w-64 ppv2-bg-white ppv2-rounded-lg ppv2-shadow-lg ppv2-border ppv2-border-gray-200 ppv2-z-50 ppv2-max-h-80 ppv2-overflow-y-auto"
        >
            <div class="ppv2-p-2">
                <div v-if="searchable" class="ppv2-mb-2 ppv2-px-2">
                    <input
                        type="text"
                        v-model="searchQuery"
                        class="ppv2-w-full ppv2-border ppv2-border-gray-300 ppv2-rounded-md ppv2-px-2 ppv2-py-1 ppv2-text-sm"
                        :placeholder="$t('common.search')"
                        @click.stop
                    />
                </div>

                <div class="ppv2-space-y-2">
                    <label
                        v-for="option in filteredOptions"
                        :key="option.id"
                        class="ppv2-flex ppv2-items-center ppv2-gap-2 ppv2-px-2 ppv2-py-1.5 ppv2-hover:bg-gray-50 ppv2-rounded ppv2-cursor-pointer"
                    >
                        <input
                            type="checkbox"
                            :checked="isSelected(option.id)"
                            @change="toggleOption(option.id)"
                            class="ppv2-rounded ppv2-border-gray-300 ppv2-text-blue-600 focus:ppv2-ring-blue-500"
                        />
                        <span class="ppv2-text-sm ppv2-text-gray-700">{{ option.label }}</span>
                    </label>
                    <div v-if="filteredOptions.length === 0" class="ppv2-px-2 ppv2-py-2 ppv2-text-gray-500 ppv2-text-center">
                        {{ $t('common.no_results') }}
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { ChevronDown } from 'lucide-vue'

export default {
    name: 'FilterDropdown',
    components: {
        ChevronDown,
    },
    props: {
        label: {
            type: String,
            required: true,
        },
        options: {
            type: Array, // [{ id: 1, label: 'Option 1' }]
            default: () => [],
        },
        value: {
            type: [String, Number, Array],
            default: () => [],
        },
        multiple: {
            type: Boolean,
            default: true,
        },
        searchable: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            isOpen: false,
            searchQuery: '',
        }
    },
    computed: {
        isActive() {
            return Array.isArray(this.value) ? this.value.length > 0 : !!this.value
        },
        selectedCount() {
            return Array.isArray(this.value) ? this.value.length : this.value ? 1 : 0
        },
        filteredOptions() {
            if (!this.searchQuery) return this.options
            return this.options.filter((opt) => opt.label.toLowerCase().includes(this.searchQuery.toLowerCase()))
        },
    },
    mounted() {
        document.addEventListener('click', this.handleClickOutside)
    },
    beforeDestroy() {
        document.removeEventListener('click', this.handleClickOutside)
    },
    methods: {
        handleClickOutside(event) {
            if (this.$el && !this.$el.contains(event.target)) {
                this.closeDropdown()
            }
        },
        toggleDropdown() {
            this.isOpen = !this.isOpen
        },
        closeDropdown() {
            this.isOpen = false
        },
        isSelected(id) {
            if (this.multiple) {
                return Array.isArray(this.value) && this.value.includes(id)
            }
            return this.value === id
        },
        toggleOption(id) {
            let newValue
            if (this.multiple) {
                newValue = [...(this.value || [])]
                const index = newValue.indexOf(id)
                if (index === -1) {
                    newValue.push(id)
                } else {
                    newValue.splice(index, 1)
                }
            } else {
                newValue = this.value === id ? null : id
                this.closeDropdown()
            }
            this.$emit('input', newValue)
            this.$emit('change', newValue)
        },
    },
}
</script>
